import { Payment } from '@interfaces/payment';
import { ISmsCreditPackage, ISmsCreditPurchase } from '@interfaces/sms-credits';
import { HttpError, useApiUrl, useCustomMutation } from '@refinedev/core';
import Response from '@responses/response';
import { convertPrice } from '@utils/resource';
import { getSmsCreditsErrorCode } from '@utils/sms-credits-error';
import { Badge, Button, Card, notification, Tag } from 'antd';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { SmsCreditPurchaseConfirmModal } from './sms-credit-purchase-confirm-modal';

type Props = {
    pkg: ISmsCreditPackage;
    defaultCard: Payment.IPaymentMethod | null;
    onPurchased: () => void;
    /** Per-credit saving vs the smallest package, whole percent (0 = no tag). */
    savingsPercent: number;
    isBestValue: boolean;
};

export const SmsCreditPackageCard: React.FC<Props> = ({
    pkg,
    defaultCard,
    onPurchased,
    savingsPercent,
    isBestValue,
}) => {
    const { t, i18n } = useTranslation(['marketplace']);
    const apiUrl = useApiUrl();
    const [open, setOpen] = useState(false);
    // Idempotency key: fresh per modal open, reused for every submit/retry from that modal.
    const purchaseIdRef = useRef<string>('');
    const [errorCode, setErrorCode] = useState<string | null>(null);
    const { mutate, isLoading } = useCustomMutation<Response<ISmsCreditPurchase>, HttpError>();

    const perThousand = convertPrice(Math.round(pkg.price / (pkg.credits / 1000)));
    const credits = pkg.credits.toLocaleString(i18n.language);

    const openModal = () => {
        purchaseIdRef.current = uuidv4();
        setOpen(true);
    };

    const close = () => {
        setOpen(false);
        setErrorCode(null);
    };

    const handleBuy = () => {
        setErrorCode(null);
        mutate(
            {
                url: `${apiUrl}/v1/sms-credits/purchases`,
                method: 'post',
                values: { packageId: pkg.id, purchaseId: purchaseIdRef.current },
                errorNotification: false,
            },
            {
                onError: (error) => {
                    const code = getSmsCreditsErrorCode(error) ?? 'unknown';
                    // A declined charge is final: the next submit is a new attempt, so it needs a new idempotency key.
                    if (code === 'payment_failed') purchaseIdRef.current = uuidv4();
                    setErrorCode(code);
                },
                onSuccess: (res) => {
                    // 'pending' = charge outcome unknown yet (info, never retry blindly).
                    // 'paid' = charged, grant still syncing (outbox) -> still a success.
                    // 'failed' = declined (defensive: the API answers a replayed decline with 400) -> error, new key.
                    const status = res?.data?.data?.status;
                    if (status === 'failed') {
                        purchaseIdRef.current = uuidv4();
                        setErrorCode('payment_failed');
                        return;
                    }
                    if (status === 'pending') {
                        notification.info({
                            message: '',
                            description: t('smsCredits.purchase.processing'),
                        });
                    } else {
                        notification.success({
                            className: 'success-notification',
                            message: '',
                            description: t(
                                status === 'granted'
                                    ? 'smsCredits.purchase.success'
                                    : 'smsCredits.purchase.successPending',
                            ),
                        });
                    }
                    close();
                    onPurchased();
                },
            },
        );
    };

    const card = (
        <Card className={`sms-credit-package-card${isBestValue ? ' is-best-value' : ''}`}>
            <h3 className="sms-credit-package-title">
                {t('smsCredits.package.credits', { credits })}
            </h3>
            <div className="sms-credit-package-price">{convertPrice(pkg.price)}</div>
            <div className="sms-credit-package-reach">
                {t('smsCredits.package.reach', {
                    sms: credits,
                    mms: Math.floor(pkg.credits / 3).toLocaleString(i18n.language),
                })}
            </div>
            <div className="sms-credit-package-meta">
                <span className="sms-credit-package-rate">
                    {t('smsCredits.package.perThousand', { price: perThousand })}
                </span>
                {savingsPercent > 0 && (
                    <Tag color="green">
                        {t('smsCredits.package.save', { percent: savingsPercent })}
                    </Tag>
                )}
            </div>
            <Button
                className="sms-credit-package-buy"
                type={isBestValue ? 'primary' : 'default'}
                block
                onClick={openModal}
                aria-label={t('smsCredits.package.buyAria', { credits })}
            >
                {t('smsCredits.package.buyFor', { price: convertPrice(pkg.price) })}
            </Button>

            <SmsCreditPurchaseConfirmModal
                open={open}
                pkg={pkg}
                defaultCard={defaultCard}
                isPaying={isLoading}
                errorCode={errorCode}
                onPay={handleBuy}
                onClose={close}
            />
        </Card>
    );

    return isBestValue ? (
        <Badge.Ribbon text={t('smsCredits.package.bestValue')} color="var(--primary)">
            {card}
        </Badge.Ribbon>
    ) : (
        card
    );
};
